import React, { Component, Fragment } from 'react'
import { observer } from 'mobx-react'
import withStyles, { ThemeProvider } from 'react-jss'
import classNames from 'classnames'
import { action, makeObservable, observable } from 'mobx'

import ConfirmationModal from 'ui/confirmation-modal/ConfirmationModal'
import { LoadingSpinner } from '@elo-kit/components/loading-spinner/LoadingSpinner'
import { SIDEBAR_POSITIONS, COURSE_THEMES, CUSTOM_CSS_FEATURE_FLAG } from 'constants/themes.constants'
import { ELOPAGE_CABINETS } from '@elo-kit/constants/general.constants'
import { COURSE_MAIN_CONTAINERS_CLASS_NAME } from '@elo-kit/constants/customCss.constants'
import { isDesktopResolution } from 'constants/browser.constants'

import Quiz from '../widgets/quiz/Quiz'
import Content from '../widgets/content/Content'
import Comments from '../widgets/comments/Comments'
import ProductAssignmentSuccess from '../widgets/content/ProductAssignment/ProductAssignmentSuccess'
import CourseBanner from '../widgets/banner/CourseBanner'
import LessonsList from '../widgets/lessons-list/LessonsList'
import LessonsNavigation from '../widgets/lessons-navigation/LessonsNavigation'
import SideNav from '../widgets/side-navigation/SideNav'
import CustomPageNavigation from '../widgets/page-navigation/CustomPageNavigation'
import customStyles from './customStyles'
import MobileLessonsNavigation from '../widgets/lessons-navigation/MobileLessonsNavigation'

const LessonPage = withStyles(customStyles)((props) => {
  const { classes, children, sideBarSide, loading, customClassName } = props
  const rightSideBar = sideBarSide === SIDEBAR_POSITIONS.right
  const leftSideBar = sideBarSide === SIDEBAR_POSITIONS.left || !sideBarSide
  const lessonPageClasses = classNames(
    'lesson-page',
    COURSE_MAIN_CONTAINERS_CLASS_NAME.lessonsContainerClassName,
    customClassName,
    rightSideBar && classes.lessonPageLeft,
    leftSideBar && classes.lessonPageRight,
    !leftSideBar && !rightSideBar && classes.lessonPage
  )
  return <div className={lessonPageClasses}>{loading ? <LoadingSpinner /> : children}</div>
})

const ContentContainer = withStyles(customStyles)(({ classes, customClassName, children }) => (
  <div
    className={classNames(
      COURSE_MAIN_CONTAINERS_CLASS_NAME.overviewContainerClassName,
      classes.contentContainer,
      customClassName,
      'course-preview'
    )}
  >
    {children}
  </div>
))

@observer
class Custom extends Component {
  @observable confirmationModal = false
  @observable footerHeight = null
  @observable desktopResolution = false

  @action toggleConfirmationModal = () => (this.confirmationModal = !this.confirmationModal)

  constructor(props) {
    super(props)
    makeObservable(this)
  }

  componentDidMount() {
    this.desktopResolution = isDesktopResolution()
    const footer = document.getElementById('footer-block')
    this.footerHeight = footer && footer.clientHeight
  }

  render() {
    const {
      courseViewsStore,
      contentPageStore,
      shopName,
      membershipThemesStore,
      sellerStore,
      sellersStore,
      preloadedContentPage,
      username,
      pathname,
      urlParams,
      queryParams,
      historyPush,
      LinkComponent,
      commentsStore,
      sellerFontsStore,
      payerStore,
      sellablesStore,
      ordersStore,
      quizzesStore,
      lessonsStore,
      communitiesStore,
      communityMemberStore,
    } = this.props
    const { item = {} } = membershipThemesStore
    const { headerHidden } = item.prefs || {}
    const {
      courseSession: { seller = {} },
      showOverviewPage,
      viewConfig: {
        isPayerCabinet,
        widgets: { quiz: displayQuiz, success: displaySuccess, content: displayContent, comments: displayComments },
      },
      handleReset,
      toggleSideMobileMenu,
      courseThemeData: { prefs = {} },
      isOverviewPage,
    } = courseViewsStore
    const { isCourseRefreshAppActive, view } = contentPageStore
    const { sidebarPosition } = prefs.lesson || {}
    const rightSideBar = sidebarPosition === SIDEBAR_POSITIONS.right
    const leftSideBar = sidebarPosition === SIDEBAR_POSITIONS.left || !sidebarPosition
    const themeProps = {
      ...prefs,
      isOverview: showOverviewPage,
      headerHidden,
    }
    const courseRefreshAppActive = isCourseRefreshAppActive && isCourseRefreshAppActive(seller.id)
    const isCustomCssAppActive =
      (sellerStore && sellerStore.isAppActive(CUSTOM_CSS_FEATURE_FLAG)) ||
      (sellersStore && sellersStore.isAppActive && sellersStore.isAppActive(CUSTOM_CSS_FEATURE_FLAG))

    const courseOverviewThemeProps = themeProps[COURSE_THEMES.courseOverview] || {}
    const courseLessonThemeProps = themeProps[COURSE_THEMES.lesson] || {}

    const showMobileNavigation =
      isPayerCabinet && !this.desktopResolution && (rightSideBar || leftSideBar || prefs.bottomNavigationEnabled)

    return (
      <ThemeProvider
        theme={{
          ...themeProps,
          footerHeight: this.footerHeight,
        }}
        key={isOverviewPage}
      >
        <Fragment>
          <div
            className={classNames(
              COURSE_MAIN_CONTAINERS_CLASS_NAME.containerClassName,
              themeProps[COURSE_THEMES.courseCustomClassName],
              'custom'
            )}
          >
            {prefs.showOverview && isOverviewPage && (
              <Fragment>
                {prefs.showOverviewBanner ? (
                  <CourseBanner
                    courseRefreshAppActive={courseRefreshAppActive}
                    toggleConfirmationModal={this.toggleConfirmationModal}
                    historyPush={historyPush}
                    desktopResolution={this.desktopResolution}
                    courseViewsStore={courseViewsStore}
                    contentPageStore={contentPageStore}
                  />
                ) : (
                  <CustomPageNavigation
                    block
                    courseRefreshAppActive={courseRefreshAppActive}
                    toggleConfirmationModal={this.toggleConfirmationModal}
                    courseViewsStore={courseViewsStore}
                  />
                )}
                <LessonsList courseViewsStore={courseViewsStore} />
              </Fragment>
            )}

            {!isOverviewPage && (
              <Fragment>
                <CourseBanner
                  courseRefreshAppActive={courseRefreshAppActive}
                  shopName={view === ELOPAGE_CABINETS.shop && shopName}
                  toggleConfirmationModal={this.toggleConfirmationModal}
                  historyPush={historyPush}
                  desktopResolution={this.desktopResolution}
                  courseViewsStore={courseViewsStore}
                  contentPageStore={contentPageStore}
                />

                <ContentContainer
                  customClassName={
                    courseOverviewThemeProps
                      ? courseOverviewThemeProps[COURSE_THEMES.courseOverviewCustomClassName]
                      : ''
                  }
                >
                  {leftSideBar && (
                    <SideNav
                      sideBarSide={SIDEBAR_POSITIONS.left}
                      username={username}
                      courseViewsStore={courseViewsStore}
                      lessonsStore={lessonsStore}
                      communitiesStore={communitiesStore}
                      communityMemberStore={communityMemberStore}
                      sellersStore={sellersStore}
                    />
                  )}
                  <LessonPage
                    sideBarSide={sidebarPosition}
                    loading={courseViewsStore.loading}
                    customClassName={
                      courseLessonThemeProps ? courseLessonThemeProps[COURSE_THEMES.courseLessonsCustomClassName] : ''
                    }
                  >
                    {displaySuccess && (
                      <ProductAssignmentSuccess
                        ordersStore={ordersStore}
                        sellablesStore={sellablesStore}
                        courseViewsStore={courseViewsStore}
                      />
                    )}
                    {displayQuiz && (
                      <Quiz
                        courseViewsStore={courseViewsStore}
                        quizzesStore={quizzesStore}
                        sellerFontsStore={sellerFontsStore}
                        payerStore={payerStore}
                      />
                    )}
                    {displayContent && (
                      <Content
                        preloadedContentPage={preloadedContentPage}
                        pathname={pathname}
                        urlParams={urlParams}
                        queryParams={queryParams}
                        historyPush={historyPush}
                        LinkComponent={LinkComponent}
                      />
                    )}
                    {prefs.bottomNavigationEnabled && (
                      <LessonsNavigation
                        courseRefreshAppActive={courseRefreshAppActive}
                        toggleConfirmationModal={this.toggleConfirmationModal}
                        desktopResolution={this.desktopResolution}
                        courseViewsStore={courseViewsStore}
                        contentPageStore={contentPageStore}
                      />
                    )}
                    {showMobileNavigation && (
                      <MobileLessonsNavigation
                        toggleSideMobileMenu={toggleSideMobileMenu}
                        rightSideBar={rightSideBar}
                        leftSideBar={leftSideBar}
                        bottomNavigationEnabled={prefs.bottomNavigationEnabled}
                        courseViewsStore={courseViewsStore}
                        contentPageStore={contentPageStore}
                      />
                    )}
                    {this.desktopResolution && (
                      <div className='fields-container__horisontal-separator fields-container__horisontal-separator--top-margin' />
                    )}
                    {displayComments && (
                      <Comments
                        courseViewsStore={courseViewsStore}
                        commentsStore={commentsStore}
                        sellerFontsStore={sellerFontsStore}
                        payerStore={payerStore}
                        sellerStore={sellerStore}
                        customView
                      />
                    )}
                  </LessonPage>
                  {rightSideBar && (
                    <SideNav
                      sideBarSide={SIDEBAR_POSITIONS.right}
                      username={username}
                      courseViewsStore={courseViewsStore}
                      lessonsStore={lessonsStore}
                      communitiesStore={communitiesStore}
                      communityMemberStore={communityMemberStore}
                      sellersStore={sellersStore}
                    />
                  )}
                </ContentContainer>
              </Fragment>
            )}

            <ConfirmationModal
              isOpen={this.confirmationModal}
              submit={() => handleReset(this.props.cmpProps)}
              submitButtonChild={
                showOverviewPage
                  ? I18n.t('react.shared.button.reset_course')
                  : I18n.t('react.shared.button.reset_lesson')
              }
              title={
                showOverviewPage
                  ? I18n.t('react.shared.confirm_course_reset')
                  : I18n.t('react.shared.confirm_lesson_reset')
              }
              toggle={this.toggleConfirmationModal}
            />
          </div>

          {isCustomCssAppActive && themeProps && themeProps.css && (
            <style
              dangerouslySetInnerHTML={{
                __html: `${themeProps.css}`,
              }}
            />
          )}
        </Fragment>
      </ThemeProvider>
    )
  }
}

Custom.displayName = 'Custom'

export default Custom
